import React, { FC, FormEvent, useMemo } from "react";
import Player from "../common/player";
import FixtureFiles from "../fixtureFiles";
import FixturePlayerControls from "../fixturePlayerControls";
import { FixtureFiles as FixtureFilesType } from "../../utils/hooks/useFixtureFootage";

import styles from "./fixturePlayer.module.scss";
import {Official} from "../../utils/hooks/useFixture";
import * as format from "../../utils/format";

export interface FixturePlayerProps {
    fileId: string;
    footage: FixtureFilesType[];
    onSourceChange: (id: string) => void;
    onToolChange: (event: FormEvent<EventTarget>) => void;
    onMenuSelection: (target: string, id: string) => void;
    officials: Official[]
    fixtureDate?: string;
}

const FixturePlayer: FC<FixturePlayerProps> = ({ fileId, footage, onToolChange, onSourceChange, onMenuSelection, officials, fixtureDate }) => {
    const invalidFileTypes = useMemo(() => {
        return !!footage.find((file) => ("AUDIO_COMMS" === file.sub_type || "VIDEO_HIGHLIGHTS" === file.sub_type) && file.id.toString() === fileId);
    }, [footage, fileId])

    return (
        <div className={styles.parentContainer}>
            <div className={styles.childContainer}>
                <div className={styles.content}>
                    <Player id={"player"}/>
                    <FixturePlayerControls onClick={onToolChange} invalidFileTypes={invalidFileTypes}/>
                    <div className={styles.bottomContent}>
                        {fixtureDate && (
                            <div className={styles.officialsHeader}>
                                <div className={styles.fixtureDate}>{format.dateTime(fixtureDate)}</div>
                                <div className={styles.officialsHeading}>Match Officials</div>
                            </div>
                        )}
                        <div className={styles.officials}>
                            {officials.map((official, index) => (
                                <div className={styles.official} key={index}>
                                    <div
                                        className={styles.position}>{convertOfficialToFriendlyName(official.position)}</div>
                                    <div
                                        className={styles.name}>{official.first_name} {official.last_name}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <FixtureFiles files={footage} onClick={onSourceChange}
                          onClickOption={onMenuSelection}></FixtureFiles>
        </div>
    );
};

const positionLabels: Record<string, string> = {
    REFEREE: "Referee",
    ASSISTANT_REFEREE_1: "Assistant Referee 1",
    ASSISTANT_REFEREE_2: "Assistant Referee 2",
    FOURTH_OFFICIAL: "Fourth Official",
    RESERVE_ASSISTANT_REFEREE: "Reserve Assistant Referee",
    VIDEO_ASSISTANT_REFEREE: "Video Assistant Referee",
    ADDITIONAL_VIDEO_ASSISTANT_REFEREE: "Additional Video Assistant Referee",
    PERFORMANCE_DEVELOPER: "Performance Developer",
    OBSERVER: "Observer",
    OBSERVER_COACH: "Observer Coach",
    PGMOL_STAFF: "PGMOL Staff",
    SHADOW_DELEGATE: "Shadow Delegate",
    DELEGATE: "Delegate",
    COACH: "Coach",
    OTHER: "Other",
};

function convertOfficialToFriendlyName(position: string): string {
    if (positionLabels[position]) {
        return positionLabels[position];
    }

    return position
        .replace(/_/g, ' ')
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.substring(1))
        .join(' ');
}

export default FixturePlayer;
